/**
 * @voltxampere/engine — Source stepping fallback (SPECS §3.7)
 *
 * When even gmin stepping fails we fall back to gradually ramping
 * up the independent sources from 10% of their nominal value to
 * 100%, in 10 steps. Each step warm-starts from the previous
 * iterate, so the first few solves face an effectively-easy
 * sub-circuit and the last solve picks up the actual answer.
 *
 * Source stepping exercises the `sourceScale` parameter on the
 * voltage and current source stamps; nonlinear stamps ignore it.
 *
 * Failure at the final step (`alpha = 1.0`) is the authoritative
 * "nope" — the orchestrator throws `ConvergenceError`. Failure at
 * earlier steps does the same; there's no further fallback after
 * source stepping in Sprint 5.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ConvergenceError } from "@voltxampere/shared";
import { newtonRaphson } from "./newton-raphson.js";
/** SPECS §3.7: 10 evenly-spaced ramp steps. */
export const SOURCE_STEPPING_SCHEDULE = [
    0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0,
];
export function sourceStepping(circuit, plan, opts) {
    const schedule = opts.schedule ?? SOURCE_STEPPING_SCHEDULE;
    let x = new Float64Array(plan.size);
    let totalIterations = 0;
    let totalClampEvents = 0;
    let totalArmijoEvents = 0;
    const stepsConverged = [];
    let lastResult;
    for (const alpha of schedule) {
        const r = newtonRaphson(circuit, plan, {
            maxIterations: opts.maxIterations,
            reltol: opts.reltol,
            abstol: opts.abstol,
            x0: x,
            sourceScale: alpha,
            ...(opts.enableArmijo !== undefined ? { enableArmijo: opts.enableArmijo } : {}),
        });
        totalIterations += r.iterations;
        totalClampEvents += r.clampEvents;
        totalArmijoEvents += r.armijoEvents;
        if (!r.converged) {
            // Sprint 5.1 P0 #3: report the *accumulated* iteration count
            // across the entire sweep up to and including this failed
            // step, not just the failing call's count. The DCSolver
            // orchestrator reads `meta.iterations` to populate
            // `DCResult.iterations`; pre-fix it lost every successful
            // step's contribution on a fallback failure.
            throw new ConvergenceError(`source stepping failed at α=${String(alpha)} after ${String(r.iterations)} iterations`, {
                meta: {
                    alpha,
                    iterations: totalIterations,
                    stepsConverged: [...stepsConverged],
                    fallback: "source",
                },
            });
        }
        stepsConverged.push(alpha);
        x = r.x;
        lastResult = r;
    }
    if (lastResult === undefined) {
        // Empty schedule. Treat as success with the cold-start zeros.
        return {
            result: {
                converged: true,
                iterations: 0,
                x,
                clampEvents: 0,
                finalResidualNorm: 0,
                armijoEvents: 0,
            },
            totalIterations: 0,
            totalClampEvents: 0,
            totalArmijoEvents: 0,
            stepsConverged: [],
        };
    }
    return {
        result: lastResult,
        totalIterations,
        totalClampEvents,
        totalArmijoEvents,
        stepsConverged,
    };
}
//# sourceMappingURL=source-stepping.js.map